/**
 * Picker data sources — API-first with demo fallback (mirrors the repo's ?demo=1
 * pattern). Each source hits the real backend contract; if it errors or demo is
 * on, canned data keeps the studio fully usable (and the dev harness working
 * without auth). Real data replaces these the moment the backend is live.
 */
import { api } from '../../../lib/api.js'
import {createSessionCache} from '../../../lib/sessionGeneration.js'
import { CANVA_FOLDERS, CANVA_ROOT_ID, FOLDER_NAMES } from './canvaFolders.js'
import { normalizeCanvaNode } from './canvaNormalize.js'
import { collectFolder } from '../studio/canvaPaging.js'
import { collectTree, filterTree } from '../studio/canvaTree.js'
import { isDemo } from './flags.js'
import { normalizeChampionships } from '../../../lib/championships.js'

export { isDemo }
export { normalizeCanvaNode }
export { filterTree }

/* -- Championships (live autocomplete) ----------------------------------- */
const DEMO_CHAMPIONSHIPS = [
  { id: 'c1', name: 'SC 4A Football State Championship', sport: 'Football', state: 'SC', level: 'High School', year: '2026', school: '', key: 'sc-4a-fb-2026' },
  { id: 'c2', name: 'Coastal Carolina Baseball — Sun Belt', sport: 'Baseball', state: 'SC', level: 'College', year: '2026', school: 'Coastal Carolina', key: 'ccu-bsb-2026' },
  { id: 'c3', name: 'Coastal Empire 5A Basketball Final', sport: 'Basketball', state: 'GA', level: 'High School', year: '2026', school: '', key: 'ga-5a-bb-2026' },
  { id: 'c4', name: 'Carolina Hurricanes — Stanley Cup', sport: 'Hockey', state: 'NC', level: 'Pro', year: '2026', school: 'Carolina Hurricanes', key: 'canes-cup-2026' },
  { id: 'c5', name: 'Alabama Hockey — ACHA National', sport: 'Hockey', state: 'AL', level: 'College', year: '2025', school: 'Alabama', key: 'bama-achahky-2025' },
  { id: 'c6', name: 'Princeton Fencing — Ivy Championship', sport: 'Fencing', state: 'NJ', level: 'College', year: '2025', school: 'Princeton', key: 'prin-fenc-2025' },
]

/**
 * Championship typeahead.
 *
 * ⚠️ THIS CALLED A ROUTE THAT DOES NOT EXIST, AND HID IT BEHIND FAKE DATA.
 * Measured on the live site, 2026-09-09:
 *
 *   GET /api/championships/search?q=…  →  404  "Cannot GET /api/championships/search"
 *
 * `sr-portal-backend/server.js` serves `/api/championships`,
 * `/api/championships/all`, `/schema`, `/feed`, `/surface` — there is no search
 * route. So the `try` here has ALWAYS failed, and the `catch` has always fallen
 * through to six hard-coded DEMO records, two of them fictional HIGH SCHOOLS
 * ("SC 4A Football State Championship", "Coastal Empire 5A Basketball Final").
 *
 * That is why the Builder's Championship field appeared to work and never
 * matched anything real, and it is one of the two sources of Brian's *"generated
 * concept should not reference a high school"* — the worse one, because a
 * fictional record could be attached to a real, recordable build.
 *
 * It now reads the SAME server-built snapshot the concept level uses — the whole
 * universe in one request — and filters client-side. **A demo record is served
 * only under an explicit `?demo=1`**, never as a silent fallback: a failure to
 * reach the universe is reported as an empty result, which is the honest answer,
 * rather than as six inventions.
 */
const champSnapshots = createSessionCache()

export async function searchChampionships(q, signal) {
  const query = (q || '').trim()
  if (!query) return []
  const ql = query.toLowerCase()

  if (isDemo()) {
    return DEMO_CHAMPIONSHIPS.filter(
      (c) => c.name.toLowerCase().includes(ql) || c.school.toLowerCase().includes(ql) ||
        c.sport.toLowerCase().includes(ql) || c.state.toLowerCase().includes(ql)
    ).slice(0, 20)
  }

  let champSnapshot
  try {
    champSnapshot = await champSnapshots.load('championships', async () => {
      const res = await api.championshipsAll({ signal })
      if (res && res.building) throw new Error('The championship snapshot is still building.')
      return normalizeChampionships(res)
    })
  } catch {
    return [] // No prior-session records or invented fallback.
  }

  const hay = (c) => `${c.name} ${c.sport} ${c.level} ${c.state} ${c.conference} ${c.season} ${c.champion}`.toLowerCase()
  return champSnapshot
    .filter((c) => !/^\s*RETIRED\b/i.test(String(c.name || '')))
    .filter((c) => hay(c).includes(ql))
    .slice(0, 20)
}

/* -- Housing catalog ------------------------------------------------------ */
const DEMO_HOUSINGS = [
  { id: 'h-classic', name: 'Classic Championship (procedural)', sport: 'All', url: '' },
  { id: 'h-alabama-hky', name: 'R4284 — Alabama Hockey 2025', sport: 'Hockey', url: '' },
  { id: 'h-conroe-fb', name: 'R3652 — Conroe Grand Oaks UIL', sport: 'Football', url: '' },
  { id: 'h-carolina-band', name: 'University of Carolina — Ring Band', sport: 'All', url: '' },
]

export async function listHousings(signal) {
  if (!isDemo()) {
    try {
      const res = await api.artworkHousings(undefined, { signal })
      const list = res?.results || res || []
      if (Array.isArray(list) && list.length) return list
    } catch {
      /* fall through */
    }
  }
  return DEMO_HOUSINGS
}


/* -- Canva FOLDER NAVIGATOR ------------------------------------------------
 *
 * Reads the CONNECTED Canva account (folder:read / design:meta) through the JWT
 * backend — the client never talks to Canva directly (hard rule #2). Two real
 * routes are tried, in order, before anything canned:
 *
 *   1. GET /api/canva/browse?folder=<id>   the folder-navigator contract:
 *                                          { breadcrumb, folders, items }
 *   2. GET /api/canva/items?folder=<id>    the route that already ships today;
 *                                          Canva's own list shape is normalized
 *                                          here into folders + items.
 *
 * Only if BOTH are unavailable does the demo tree render — and it is flagged as
 * demo in the UI so a rep never mistakes canned designs for the real library.
 * ---------------------------------------------------------------------- */

/* -- demo tree (fallback only) ------------------------------------------- */
function swatchThumb(hex, glyph = '') {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="${hex}"/><circle cx="50" cy="50" r="30" fill="#fff" opacity="0.22"/><text x="50" y="58" font-family="system-ui,sans-serif" font-size="26" font-weight="700" fill="#fff" text-anchor="middle" opacity="0.9">${glyph}</text></svg>`
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
}

// Keyed by the REAL folder ids so the demo tree lines up with live navigation.
const R = CANVA_ROOT_ID
const B = CANVA_FOLDERS.bezel.id
const S = CANVA_FOLDERS.shanks.id

const DEMO_CANVA = {
  [R]: {
    id: R,
    name: 'SR Artwork Engine',
    folders: [
      { id: B, name: 'Bezel & Top Designs' },
      { id: S, name: 'Side Shanks' },
      { id: 'demo-backgrounds', name: 'Backgrounds' },
    ],
    items: [],
  },
  [B]: {
    id: B,
    name: 'Bezel & Top Designs',
    folders: [
      { id: 'demo-bezel-fb', name: 'Football' },
      { id: 'demo-bezel-bb', name: 'Basketball' },
    ],
    items: [{ id: 'bz-domed', name: 'Domed Crest', thumb: swatchThumb('#c7a24a', '◆') }],
  },
  'demo-bezel-fb': {
    id: 'demo-bezel-fb',
    name: 'Football',
    folders: [],
    items: [
      { id: 'bz-fb-1', name: 'Football Faceted Top', thumb: swatchThumb('#8a5a2b', '🏈') },
      { id: 'bz-fb-2', name: 'Football Enamel Emblem', thumb: swatchThumb('#123c69', '🏈') },
    ],
  },
  [S]: {
    id: S,
    name: 'Side Shanks',
    folders: [
      { id: 'demo-shank-fb', name: 'Football' },
      { id: 'demo-shank-bb', name: 'Basketball' },
      { id: 'demo-shank-wr', name: 'Wrestling' },
    ],
    items: [],
  },
  'demo-shank-fb': {
    id: 'demo-shank-fb',
    name: 'Football',
    folders: [],
    items: [
      { id: 'sh-fb-mascot', name: 'Sculpted Mascot Shank', thumb: swatchThumb('#6b0f1a', '★') },
      { id: 'sh-fb-year', name: 'Engraved Year & Sport', thumb: swatchThumb('#274060', '26') },
    ],
  },
  'demo-backgrounds': {
    id: 'demo-backgrounds',
    name: 'Backgrounds',
    folders: [],
    items: [
      { id: 'bg-stadium', name: 'Stadium Night', thumb: swatchThumb('#101820', '▦') },
      { id: 'bg-banner', name: 'Championship Banner', thumb: swatchThumb('#7a1220', '▦') },
    ],
  },
}

function demoNode(folderId) {
  const node = DEMO_CANVA[folderId] || DEMO_CANVA[R]
  return { ...node, breadcrumb: null, source: 'demo' }
}

/**
 * A page that could not be read. Its `source` deliberately DIFFERS from a good
 * page's `'canva'`, which is how `collectFolder` tells "the walk was cut short"
 * apart from "the folder ended" — it keeps every page already collected, flags
 * the node `partial` and carries this reason onto it.
 */
function stopPage(id, cont, reason) {
  return {
    id,
    name: FOLDER_NAMES[id] || '',
    folders: [],
    items: [],
    continuation: null,
    source: cont ? 'canva-interrupted' : 'canva',
    reason,
  }
}

/**
 * Browse one Canva folder, FOLLOWING ITS CONTINUATION TOKEN TO EXHAUSTION.
 *
 * @param {string} folderId
 * @param {AbortSignal} [signal]
 * @param {{ onPage?: (node) => void, continuation?: string|null, seed?: object|null }} [opts]
 *        `onPage` lands each page on screen as it arrives, so a 300+ design
 *        folder starts rendering on page 1 instead of after the last one.
 *        `continuation` + `seed` are the "Load more" path for a folder deeper
 *        than `PAGE_CAP`.
 * @returns {{ id, name, folders, items, breadcrumb, continuation, partial,
 *             reason, source: 'canva'|'demo' }}
 *          `source` drives the "demo library" badge in the navigator.
 */
export async function browseCanva(folderId = CANVA_ROOT_ID, signal, opts = {}) {
  const id = folderId || CANVA_ROOT_ID
  if (isDemo()) return demoNode(id)

  // 1. the folder-navigator contract
  try {
    const res = await api.canvaBrowse({ folder: id }, { signal })
    const node = normalizeCanvaNode(res, id)
    if (node) return node
  } catch (err) {
    if (err?.name === 'AbortError') throw err
  }

  // 2. the route that ships today. Its parameter is `folderId` (server.js:
  // `const { folderId, q, continuation } = req.query`) — passing `folder` made
  // every live call return an empty list and silently drop to the demo tree.
  //
  // AND IT PAGES. The route answers one page plus a `continuation` token; a
  // folder like Bezel & Top (300+ designs) is several pages, so the token is
  // followed to exhaustion here rather than showing the first page as if it
  // were the folder.
  try {
    // Page 1 failing is "the feed is not answering" and falls through to the
    // canned tree below. A LATER page failing is a partial load, and losing 250
    // already-loaded designs to a flaky page 6 is worse than showing 250 and
    // saying so — `collectFolder` keeps them and marks the node partial.
    let firstFailed = false
    const fetchPage = async (cont) => {
      const query = cont ? { folderId: id, continuation: cont } : { folderId: id }
      try {
        const res = await api.canvaItems(query, { signal })
        const node = normalizeCanvaNode(res, id)
        if (node) return node
        if (!cont) firstFailed = true
        return stopPage(id, cont, 'The Canva feed answered in a shape this folder could not read.')
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        if (!cont) firstFailed = true
        return stopPage(id, cont, err?.status === 503
          ? 'The live Canva feed is not connected on the backend yet.'
          : `The Canva feed stopped answering part-way through this folder (${err?.status || 'network error'}).`)
      }
    }

    const all = await collectFolder(fetchPage, {
      start: opts.continuation || null,
      seed: opts.seed || null,
      // Don't paint a failed page-1 snapshot on screen only to replace it with
      // the canned tree a tick later.
      onPage: (snap) => { if (!firstFailed && opts.onPage) opts.onPage(snap) },
    })

    if (!firstFailed) {
      return {
        ...all,
        name: all.name || FOLDER_NAMES[id] || '',
        // The trail is kept client-side by the navigator, so no breadcrumb from
        // the feed is not a problem — but it must be declared, not inherited.
        breadcrumb: null,
        source: 'canva',
      }
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err
  }

  // 3. canned — clearly flagged in the UI
  return demoNode(id)
}

/**
 * Browse a Canva folder AND EVERY FOLDER UNDER IT.
 *
 * `browseCanva` walks ONE folder's pages to exhaustion — that is depth, and it
 * has been right since v1.36.1. This is the BREADTH half that was never built:
 * the element folders (Bezel & Top Designs, Side Shanks) keep a handful of
 * designs at the top level and the rest in SPORT SUB-FOLDERS, so the navigator
 * was rendering a dozen tiles plus a row of folders and reading, correctly, as
 * "that is all there is". `collectTree` is the same walk the Builder's picker
 * uses (studio/canvaTree.js), so the two surfaces cannot drift on this either.
 *
 * Sub-folders still come back on `folders`, so drill-down and the breadcrumb
 * work unchanged underneath the flattened view.
 *
 * @param {string} folderId
 * @param {AbortSignal} [signal]
 * @param {{ onProgress?: (node) => void }} [opts]
 */
export async function browseCanvaDeep(folderId = CANVA_ROOT_ID, signal, opts = {}) {
  const id = folderId || CANVA_ROOT_ID
  const tree = await collectTree(
    (fid, o) => browseCanva(fid, signal, { onPage: o?.onPage }),
    { id, name: FOLDER_NAMES[id] || '' },
    { signal, onProgress: opts.onProgress }
  )
  return { ...tree, breadcrumb: null, source: tree.source || 'canva' }
}
